// Main reusable Support & FAQ's component
// When syncUrl=true (default), the active screen is reflected in the URL via
// ?faq= / ?category= / ?article= so individual FAQs/articles can be deep-linked
// and shared. When syncUrl=false, falls back to internal stack-based nav — use
// this for iframe embeds where URL mutation is undesirable.

import { useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box } from '@/ui';
import SupportLanding from './SupportLanding';
import CategoryView from './CategoryView';
import FaqDetail from './FaqDetail';
import ArticleDetail from './ArticleDetail';
import { getCategoryById, getFaqById, getArticleById } from '../data/supportFaqData';

export interface SupportAndFaqsProps {
  onContactSupport?: () => void;
  isAuthenticated?: boolean;
  syncUrl?: boolean;
}

type Screen =
  | { type: 'landing' }
  | { type: 'category'; categoryId: string }
  | { type: 'faq'; faqId: string }
  | { type: 'article'; articleId: string };

const PARAM_FAQ = 'faq';
const PARAM_CATEGORY = 'category';
const PARAM_ARTICLE = 'article';

function readScreenFromParams(params: URLSearchParams): Screen {
  const faqId = params.get(PARAM_FAQ);
  if (faqId && getFaqById(faqId)) return { type: 'faq', faqId };
  const articleId = params.get(PARAM_ARTICLE);
  if (articleId && getArticleById(articleId)) return { type: 'article', articleId };
  const categoryId = params.get(PARAM_CATEGORY);
  if (categoryId && getCategoryById(categoryId)) return { type: 'category', categoryId };
  return { type: 'landing' };
}

function paramsForScreen(screen: Screen): URLSearchParams {
  const p = new URLSearchParams();
  if (screen.type === 'faq') p.set(PARAM_FAQ, screen.faqId);
  else if (screen.type === 'category') p.set(PARAM_CATEGORY, screen.categoryId);
  else if (screen.type === 'article') p.set(PARAM_ARTICLE, screen.articleId);
  return p;
}

function SupportAndFaqs({ onContactSupport, isAuthenticated = true, syncUrl = true }: SupportAndFaqsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [internalStack, setInternalStack] = useState<Screen[]>([{ type: 'landing' }]);

  // Counts pushes we've made within this page, so goBack can tell whether
  // navigate(-1) would stay inside the app (in-page history) or leave the site
  // entirely (user arrived via an external deep link). In the latter case we
  // fall back to clearing params (lands on landing) rather than popping out.
  const pushDepth = useRef(0);

  const topRef = useRef<HTMLDivElement>(null);

  const currentScreen: Screen = syncUrl
    ? readScreenFromParams(searchParams)
    : (internalStack[internalStack.length - 1] ?? { type: 'landing' });

  // Walk up to the nearest scrollable ancestor and reset scrollTop to 0 so the
  // container's own top padding (header spacer, py:4) also scrolls into view.
  const scrollToTop = useCallback(() => {
    const el = topRef.current;
    if (el) {
      let node: HTMLElement | null = el.parentElement;
      while (node) {
        const style = window.getComputedStyle(node);
        const canScroll = /(auto|scroll|overlay)/.test(
          style.overflowY + style.overflow
        );
        if (canScroll && node.scrollHeight > node.clientHeight) {
          node.scrollTo({ top: 0, behavior: 'smooth' });
          break;
        }
        node = node.parentElement;
      }
    }
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (typeof document !== 'undefined') {
      document.documentElement.scrollTo?.({ top: 0, behavior: 'smooth' });
      document.body.scrollTo?.({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const pushScreen = useCallback((screen: Screen) => {
    if (syncUrl) {
      pushDepth.current += 1;
      setSearchParams(paramsForScreen(screen));
    } else {
      setInternalStack(prev => [...prev, screen]);
    }
    scrollToTop();
  }, [syncUrl, setSearchParams, scrollToTop]);

  const goBack = useCallback(() => {
    if (syncUrl) {
      if (pushDepth.current > 0) {
        pushDepth.current -= 1;
        navigate(-1);
      } else {
        setSearchParams(new URLSearchParams());
      }
    } else {
      setInternalStack(prev => (prev.length <= 1 ? prev : prev.slice(0, -1)));
    }
    scrollToTop();
  }, [syncUrl, navigate, setSearchParams, scrollToTop]);

  const handleSelectCategory = useCallback((categoryId: string) => {
    pushScreen({ type: 'category', categoryId });
  }, [pushScreen]);

  const handleSelectFaq = useCallback((faqId: string) => {
    pushScreen({ type: 'faq', faqId });
  }, [pushScreen]);

  const handleSelectArticle = useCallback((articleId: string) => {
    pushScreen({ type: 'article', articleId });
  }, [pushScreen]);

  const handleContactSupport = useCallback(() => {
    if (onContactSupport) {
      onContactSupport();
    }
  }, [onContactSupport]);

  return (
    <Box data-testid="features-support-faqs-components-support-and-faqs-container-action" ref={topRef} sx={{ py: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2, scrollMarginTop: 0 }}>
      {(() => {
        switch (currentScreen.type) {
          case 'landing':
            return (
              <SupportLanding
                onSelectCategory={handleSelectCategory}
                onSelectFaq={handleSelectFaq}
              />
            );
          case 'category':
            return (
              <CategoryView
                categoryId={currentScreen.categoryId}
                onBack={goBack}
                onSelectFaq={handleSelectFaq}
                onSelectCategory={handleSelectCategory}
              />
            );
          case 'faq':
            return (
              <FaqDetail
                faqId={currentScreen.faqId}
                onBack={goBack}
                onSelectFaq={handleSelectFaq}
                onSelectArticle={handleSelectArticle}
                onContactSupport={handleContactSupport}
                isAuthenticated={isAuthenticated}
              />
            );
          case 'article':
            return (
              <ArticleDetail
                articleId={currentScreen.articleId}
                onBack={goBack}
                onContactSupport={handleContactSupport}
                isAuthenticated={isAuthenticated}
              />
            );
          default:
            return null;
        }
      })()}
    </Box>
  );
}

export default SupportAndFaqs;
